import React from "react";
import { Pie, PieChart, Cell } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@components/ui/chart";
import { Skeleton } from "@components/ui/skeleton";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

interface SensorYieldProps {
  currentPeriodData: {
    date: string;
    failedSensorChecks: number;
    successfulSensorChecks: number;
  }[];
  previousPeriodData: {
    date: string;
    failedSensorChecks: number;
    successfulSensorChecks: number;
  }[];
  loading: boolean;
}

export const SensorYield = ({
  currentPeriodData,
  previousPeriodData,
  loading,
}: SensorYieldProps) => {
  if (loading) {
    return <Skeleton className="h-full min-h-[220px] w-full rounded-lg" />;
  }

  // Current Period
  const totalSuccessfulCurrent = currentPeriodData.reduce(
    (sum, day) => sum + day.successfulSensorChecks,
    0
  );
  const totalFailedCurrent = currentPeriodData.reduce(
    (sum, day) => sum + day.failedSensorChecks,
    0
  );
  const totalChecksCurrent = totalSuccessfulCurrent + totalFailedCurrent;
  // Previous Period
  const totalSuccessfulPrevious = previousPeriodData.reduce(
    (sum, day) => sum + day.successfulSensorChecks,
    0
  );
  const totalFailedPrevious = previousPeriodData.reduce(
    (sum, day) => sum + day.failedSensorChecks,
    0
  );
  const totalChecksPrevious = totalSuccessfulPrevious + totalFailedPrevious;

  const yieldCurrent =
    totalChecksCurrent > 0
      ? Math.round((totalSuccessfulCurrent / totalChecksCurrent) * 100)
      : 0;
  const yieldPrevious =
    totalChecksPrevious > 0
      ? Math.round((totalSuccessfulPrevious / totalChecksPrevious) * 100)
      : 0;

  const yieldChange = yieldCurrent - yieldPrevious;
  const isPositive = yieldChange >= 0;

  const chartConfig = {
    pass: {
      label: "Pass",
      color: "#22c55e",
    },
    fail: {
      label: "Fail",
      color: "#ef4444",
    },
  } as const;

  const chartData = [
    {
      name: "pass",
      label: "Pass",
      value: totalSuccessfulCurrent,
    },
    {
      name: "fail",
      label: "Fail",
      value: totalFailedCurrent,
    },
  ];

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="text-2xl font-semibold">{yieldCurrent}%</span>
        {totalChecksPrevious > 0 && (
          <span
            className={`flex items-center gap-1 text-sm font-medium ${
              isPositive ? "text-emerald-600" : "text-rose-500"
            }`}
          >
            {isPositive ? (
              <ArrowUpRight className="size-4" aria-hidden="true" />
            ) : (
              <ArrowDownRight className="size-4" aria-hidden="true" />
            )}
            {Math.abs(yieldChange).toFixed(1)}%
          </span>
        )}
      </div>
      {totalChecksCurrent === 0 ? (
        <div className="flex h-full min-h-[220px] items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
          No sensor checks recorded for this period.
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className="mx-auto flex h-full min-h-[260px] w-full flex-1 flex-col justify-center !aspect-auto"
        >
          <PieChart>
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name, item) => (
                    <span className="font-medium">
                      {Number(value).toLocaleString()} checks
                    </span>
                  )}
                />
              }
            />
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="label"
              innerRadius="55%"
              outerRadius="80%"
              strokeWidth={0}
              paddingAngle={4}
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={`var(--color-${entry.name})`} />
              ))}
            </Pie>
            <ChartLegend content={<ChartLegendContent />} />
          </PieChart>
        </ChartContainer>
      )}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-muted-foreground">Pass</p>
          <p className="font-semibold text-emerald-600">
            {totalSuccessfulCurrent.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-muted-foreground">Fail</p>
          <p className="font-semibold text-rose-500">
            {totalFailedCurrent.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
};
